import type { AgentTool } from '@saaga/shared/interfaces/agent-tool.interface'
import type { ReactNode } from 'react'
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form'

import { ToolListManager } from '@/components/molecules/tool-list-manager.molecule'
import { ToolSelectorSearchable } from '@/components/molecules/tool-selector-searchable.molecule'
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form'

import type { AgentNodeFormSchemaType } from '../agency-node-form.schema'
import { useToolsHandlers } from './agent-node-tools-handlers.hook'

export interface AgentNodeToolsSectionProps {
  form: UseFormReturn<AgentNodeFormSchemaType>
  toolList: AgentTool[]
  isLoadingTools: boolean
}

interface ToolsFieldContentProps {
  field: ControllerRenderProps<AgentNodeFormSchemaType, 'toolList'>
  toolList: AgentTool[]
  isLoadingTools: boolean
}

const ToolsFieldContent = ({
  field,
  toolList,
  isLoadingTools,
}: ToolsFieldContentProps): ReactNode => {
  const { selectedToolList, handleToggle, handleSelectAll, handleDeselectAll, handleRemove } =
    useToolsHandlers(field, toolList)

  return (
    <FormItem>
      <FormLabel>Tools</FormLabel>
      <div className="agent-node-tools-section space-y-3">
        <FormControl>
          <ToolSelectorSearchable
            selectedIdList={field.value}
            onToggle={handleToggle}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            toolList={toolList}
            isLoading={isLoadingTools}
            placeholder="Add a tool..."
            className="agent-node-tool-select"
          />
        </FormControl>
        <ToolListManager
          selectedToolList={selectedToolList}
          onRemove={handleRemove}
          emptyMessage="No tools selected"
        />
      </div>
      <FormDescription>Tools this agent can use (e.g., web search, code execution)</FormDescription>
    </FormItem>
  )
}

/**
 * Tools configuration section for agent node drawer.
 * Allows selecting and managing tools that the agent can use.
 */
export const AgentNodeToolsSection = ({
  form,
  toolList,
  isLoadingTools,
}: AgentNodeToolsSectionProps): ReactNode => (
  <FormField
    control={form.control}
    name="toolList"
    render={({ field }) => (
      <ToolsFieldContent field={field} toolList={toolList} isLoadingTools={isLoadingTools} />
    )}
  />
)

AgentNodeToolsSection.displayName = 'AgentNodeToolsSection'
